/* eslint-disable */
// @ts-nocheck
import { BrandCheckSchemas } from "@mg/schemas";

import { AI_URL, buildBrandcheckUrl } from "../config/env";
import { smartFetch } from "../utils/http";

// Create BrandCheck API base URL
// Based on the brandcheck service being part of rembrandt service
const BRANDCHECK_API_URL = buildBrandcheckUrl(AI_URL, "brandcheck");

/**
 * BrandCheck Service - API client for brand analysis
 *
 * Provides methods to interact with the BrandCheck service endpoints:
 * - Session management
 * - Research process monitoring
 * - Analysis results retrieval
 * - Interactive chat with AI agent
 */

/**
 * Create or find existing brandcheck session based on email domain or company name
 *
 * @param payload - Email and/or company name to analyze
 * @returns Session information with session_id for use in other endpoints
 */
export async function createOrGetSession(
  payload: BrandCheckSchemas.SessionRequest,
): Promise<BrandCheckSchemas.SessionResponse> {
  const json = await smartFetch(`${BRANDCHECK_API_URL}/session`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  const parsedData = BrandCheckSchemas.SessionResponseSchema.parse(json);
  return parsedData;
}

/**
 * Sort gathering data in the specified order: BRAND -> COMPLIANCE -> BRAND_ASSET
 * with 'Locked' separators between each category section
 *
 * @param gathering - Array of gathering items from the API
 * @returns Sorted array with proper 'Locked' separators
 */
function sortGatheringData(gathering: any[]): any[] {
  // Remove any existing 'Locked' category items
  const filteredGathering = gathering.filter(
    (item) => item.category !== "Locked",
  );

  // Group items by category
  const brandItems = filteredGathering.filter(
    (item) => item.category === "BRAND",
  );
  const complianceItems = filteredGathering.filter(
    (item) => item.category === "COMPLIANCE",
  );
  const brandAssetItems = filteredGathering.filter(
    (item) => item.category === "BRAND_ASSET",
  );

  const result: any[] = [];

  // Add BRAND items
  if (brandItems.length > 0) {
    result.push(...brandItems);
    result.push({
      category: "Locked",
      tag: "Locked",
      message: "Finalizing BRAND...",
    });
  }

  // Add COMPLIANCE items
  if (complianceItems.length > 0) {
    result.push(...complianceItems);
    result.push({
      category: "Locked",
      tag: "Locked",
      message: "Finalizing COMPLIANCE...",
    });
  } else {
    // If no COMPLIANCE items, still add the locked separator
    result.push({
      category: "Locked",
      tag: "Locked",
      message: "Finalizing COMPLIANCE...",
    });
  }

  // Add BRAND_ASSET items
  if (brandAssetItems.length > 0) {
    result.push(...brandAssetItems);
    result.push({
      category: "Locked",
      tag: "Locked",
      message: "Finalizing BRAND_ASSET...",
    });
  } else {
    // If no BRAND_ASSET items, still add the locked separator
    result.push({
      category: "Locked",
      tag: "Locked",
      message: "Finalizing BRAND_ASSET...",
    });
  }

  return result;
}

/**
 * Get AI agent research process and statistics
 *
 * @param sessionId - Session ID from createOrGetSession
 * @returns Research tasks, thoughts, and analysis statistics
 */
export async function getResearchProgress(sessionId: string) {
  const json = await smartFetch(`${BRANDCHECK_API_URL}/research/${sessionId}`, {
    method: "GET",
  });
  const sortedResponse = {
    ...(json.research ?? {}),
    gathering: sortGatheringData(json.research?.gathering ?? []),
  };
  return sortedResponse;
}

/**
 * Get final brand risk analysis results
 *
 * @param sessionId - Session ID from createOrGetSession
 * @returns Structured risks with issue levels, sources, and summary statistics
 */
export async function getAnalysisResults(sessionId: string) {
  const json = await smartFetch(`${BRANDCHECK_API_URL}/results/${sessionId}`, {
    method: "GET",
  });
  return json;
}

/**
 * Chat with AI agent about analysis findings
 *
 * @param payload - Session ID and message to send
 * @returns Complete chat conversation including user message and AI response
 */
export async function chatWithAgent(
  payload: BrandCheckSchemas.ChatRequest,
): Promise<BrandCheckSchemas.ChatResponse> {
  const json = await smartFetch(`${BRANDCHECK_API_URL}/chat`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  return json;
}

/**
 * Check BrandCheck service health and get endpoint information
 *
 * @returns Service health status and available endpoints
 */
export async function getServiceHealth(): Promise<BrandCheckSchemas.HealthResponse> {
  const json = await smartFetch(`${BRANDCHECK_API_URL}/health`, {
    method: "GET",
  });

  const parsedData = BrandCheckSchemas.HealthResponseSchema.parse(json);
  return parsedData;
}

/**
 * Polling helper for research completion
 *
 * @param sessionId - Session ID to monitor
 * @param onProgress - Callback for progress updates
 * @param maxAttempts - Maximum polling attempts (default: 20)
 * @param intervalMs - Polling interval in ms (default: 2000)
 * @returns Final research response when completed
 */
export async function pollResearchUntilComplete(
  sessionId: string,
  onProgress?: (progress: BrandCheckSchemas.ResearchResponse) => void,
  maxAttempts = 20,
  intervalMs = 2000,
): Promise<BrandCheckSchemas.ResearchResponse> {
  let attempts = 0;

  while (attempts < maxAttempts) {
    const progress = await getResearchProgress(sessionId);

    if (onProgress) {
      onProgress(progress);
    }

    if (progress.status === "completed") {
      return progress;
    }

    if (progress.status === "failed") {
      throw new Error("Research failed");
    }

    attempts++;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error("Research polling timeout - exceeded maximum attempts");
}

/**
 * Complete brandcheck workflow helper
 *
 * This function handles the complete brandcheck workflow:
 * 1. Create/get session
 * 2. Poll research until complete
 * 3. Get final results
 *
 * @param input - Email and/or company name
 * @param onResearchProgress - Callback for research progress updates
 * @returns Complete brandcheck results
 */
export async function runCompleteBrandCheck(
  input: BrandCheckSchemas.SessionRequest,
  onResearchProgress?: (progress: BrandCheckSchemas.ResearchResponse) => void,
): Promise<{
  session: BrandCheckSchemas.SessionResponse;
  research: BrandCheckSchemas.ResearchResponse;
  results: BrandCheckSchemas.ResultsResponse;
}> {
  // Step 1: Create or get session
  const session = await createOrGetSession(input);

  // Step 2: Poll research until complete
  const research = await pollResearchUntilComplete(
    session.session_id,
    onResearchProgress,
  );

  // Step 3: Get final results
  const results = await getAnalysisResults(session.session_id);

  return {
    session,
    research,
    results,
  };
}

/**
 * Error handling helper for BrandCheck API responses
 */
export function handleBrandCheckError(error: unknown): string {
  if (error instanceof Error) {
    try {
      // Try to parse as BrandCheck API error response
      const errorData = JSON.parse(error.message);
      const parsedError =
        BrandCheckSchemas.ErrorResponseSchema.parse(errorData);
      return (
        parsedError.detail ||
        parsedError.error ||
        parsedError.message ||
        "Unknown error"
      );
    } catch {
      return error.message;
    }
  }

  return "An unexpected error occurred";
}

/**
 * Utility functions for BrandCheck data processing
 */
export const BrandCheckUtils = {
  /**
   * Get risk color based on status level
   */
  getRiskColor: (status: BrandCheckSchemas.Risk["status"]): string => {
    switch (status) {
      case "low":
        return "#10B981"; // green
      case "medium":
        return "#F59E0B"; // amber
      case "high":
        return "#DC2626"; // dark red
      default:
        return "#6B7280"; // gray
    }
  },

  /**
   * Get risk priority score for sorting
   */
  getRiskPriority: (status: BrandCheckSchemas.Risk["status"]): number => {
    switch (status) {
      case "high":
        return 3;
      case "medium":
        return 2;
      case "low":
        return 1;
      default:
        return 0;
    }
  },

  /**
   * Sort risks by priority (critical first)
   */
  sortRisksByPriority: (
    risks: BrandCheckSchemas.Risk[],
  ): BrandCheckSchemas.Risk[] => {
    return [...risks].sort(
      (a, b) =>
        BrandCheckUtils.getRiskPriority(b.status) -
        BrandCheckUtils.getRiskPriority(a.status),
    );
  },

  /**
   * Group risks by category
   */
  groupRisksByCategory: (
    risks: BrandCheckSchemas.Risk[],
  ): Record<string, BrandCheckSchemas.Risk[]> => {
    return risks.reduce(
      (acc, risk) => {
        if (!acc[risk.category]) {
          acc[risk.category] = [];
        }
        acc[risk.category].push(risk);
        return acc;
      },
      {} as Record<string, BrandCheckSchemas.Risk[]>,
    );
  },

  /**
   * Calculate overall risk score
   */
  calculateOverallRiskScore: (risks: BrandCheckSchemas.Risk[]): number => {
    if (risks.length === 0) return 100;

    const totalScore = risks.reduce((sum, risk) => {
      const weight = BrandCheckUtils.getRiskPriority(risk.status);
      return sum + weight * (100 - risk.trust_percentage);
    }, 0);

    const maxPossibleScore = risks.length * 4 * 100; // 4 = max priority, 100 = max untrust
    const riskPercentage = (totalScore / maxPossibleScore) * 100;

    return Math.max(0, 100 - riskPercentage);
  },

  /**
   * Format research step display name
   */
  formatResearchStep: (step: string): string => {
    return step
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  },

  /**
   * Get suggested chat questions based on results
   */
  getSuggestedQuestions: (
    results: BrandCheckSchemas.ResultsResponse,
  ): string[] => {
    const suggestions = [...(results.chat_suggestions || [])];

    // Add dynamic suggestions based on findings
    if (results.summary.by_status.high > 0) {
      suggestions.unshift(
        "What are the most critical issues I should address immediately?",
      );
    }

    if (results.summary.by_category.reputation > 0) {
      suggestions.push("How can I improve my brand's online reputation?");
    }

    if (results.summary.by_category.security > 0) {
      suggestions.push("What security improvements should I prioritize?");
    }

    return suggestions.slice(0, 5); // Limit to 5 suggestions
  },
};
